import { defineNuxtPlugin, useRouter, useRuntimeConfig } from '#imports'
import { isNavigationFailure } from '#vue-router'
import { setup, type FacebookQuery } from '../pixel/meta-pixel';
import { minimatch } from 'minimatch'
import type { Plugin } from 'nuxt/app';

type PixelConfig = {
    id: string
    autoconfig: boolean
    consent: string,
    pageView: string
}

/**
 * Some environments may pass boolean values as strings. This function
 * will parse the value and return a boolean.
 *
 * @param value - The value to parse
 * @returns - The boolean interpretation of the value
 */
const parseStringOrBoolean = (value: string | boolean | undefined) => {
    if (typeof value === "string") {
        return value === "true";
    }

    return value;
};

export default defineNuxtPlugin(async () => {
    console.debug('[Meta-Pixel Client]: Init.');

    // Immediately-invoked async function that merges the static module
    // configuration (from runtimeConfig) with dynamic settings fetched
    // from the API. API values override the module defaults.
    const metapixel = await (async () => {
        const runtimeConfig = useRuntimeConfig();
        const config: any = await useApi('/setting/meta');

        return {
            ...runtimeConfig.public.metapixel,
            ...config
        };
    })();

    const pixels: Record<string, PixelConfig> = metapixel.pixels;
    const isDebug = parseStringOrBoolean(metapixel.debug);
    const isEnabled = parseStringOrBoolean(metapixel.enabled);

    console.warn(isDebug);

    const { $fbq, init, pageView } = setup();

    if (isEnabled) {
        if (isDebug) {
            console.debug("[Meta-Pixel] Plugin is enabled.");
        }

        for (const { id, autoconfig, consent } of Object.values(pixels)) {
            init(id, autoconfig, consent);
        }

        const router = useRouter();

        router.afterEach((to: any, _: any, failure: any) => {
            if (isNavigationFailure(failure)) return;

            for (const { id, pageView: path } of Object.values(pixels)) {
                const match = minimatch(to.path, path ?? '**');

                if (match) {
                    pageView(id);
                }
            }
        });
    } else {
        if (isDebug) {
            console.debug("[Meta-Pixel] Plugin has been disabled.");
        }
    }

    return {
        provide: {
            fbq: $fbq
        }
    }
}) as Plugin<{ fbq: FacebookQuery }>